import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import { supabaseConfig } from '../config/supabase'
import Title from '../components/shared/Title'
import Footer from '../components/layout/Footer'

let supabase
try {
  supabase = createClient(supabaseConfig.url, supabaseConfig.anonKey)
} catch (error) {
  console.error('Supabase initialization error:', error)
}

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    dob: '', // Changed from dateOfBirth to match schema
    batch: 2025,
    mobile: '',
    email: '', // Added email field
    socials: { // Changed from socialMedia to match schema
      linkedin: '',
      instagram: '',
      twitter: ''
    }
  })

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null
  })

  const [initError, setInitError] = useState(null)

  useEffect(() => {
    if (!supabase) {
      setInitError('Failed to initialize database connection')
    }
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const validateForm = () => {
    if (!formData.name.trim()) return 'Name is required'
    if (!formData.dob) return 'Date of Birth is required'
    if (!formData.mobile.trim()) return 'Mobile number is required'
    if (!formData.email.trim()) return 'Email is required'
    if (!/^[0-9]{10}$/.test(formData.mobile)) return 'Invalid mobile number format'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format'
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!supabase) {
      setFormStatus({
        loading: false,
        success: false,
        error: 'Database connection error. Please try again later.'
      })
      return
    }

    // Validate form
    const validationError = validateForm()
    if (validationError) {
      setFormStatus({
        loading: false,
        success: false,
        error: validationError
      })
      return
    }

    setFormStatus({
      loading: true,
      success: false,
      error: null
    })

    try {
      const { data, error } = await supabase
        .from('batch25')
        .insert([
          {
            name: formData.name,
            dob: formData.dob,
            batch: formData.batch,
            mobile: formData.mobile,
            email: formData.email,
            socials: formData.socials
          }
        ])
        .single()

      if (error) throw error

      setFormStatus({
        loading: false,
        success: true,
        error: null
      })

      // Reset form after successful submission
      setFormData({
        name: '',
        dob: '',
        batch: 2025,
        mobile: '',
        email: '',
        socials: {
          linkedin: '',
          instagram: '',
          twitter: ''
        }
      })
    } catch (error) {
      setFormStatus({
        loading: false,
        success: false,
        error: error.message
      })
    }
  }

  if (initError) {
    return (
      <div className="app">
        <main>
          <div className="register">
            <div className="container">
              <div className="register__error">
                <h3>Connection Error</h3>
                <p>{initError}</p>
                <button 
                  className="button button--secondary"
                  onClick={() => window.location.reload()}
                >
                  Retry Connection
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="app">
      <main>
        <div className="register">
          <div className="container">
            <Title
              tagline="Join Our Community"
              heading="2025 Batch Registration"
              subheading="Complete your alumni profile"
            />

            {formStatus.success ? (
              <div className="register__success">
                <h3>Registration Successful! 🎉</h3>
                <p>Thank you for registering with RAYS. We'll be in touch soon.</p>
                <button 
                  className="button button--secondary"
                  onClick={() => setFormStatus(prev => ({ ...prev, success: false }))}
                >
                  Register Another
                </button>
              </div>
            ) : (
              <form className="register__form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dob">Date of Birth *</label>
                  <input
                    type="date"
                    id="dob"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="batch">Batch</label>
                  <input
                    type="number"
                    id="batch"
                    value={2025}
                    disabled
                    className="disabled-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobile">Mobile Number *</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Social Media Profiles (Optional)</label>
                  <input
                    type="url"
                    placeholder="LinkedIn Profile URL"
                    name="socials.linkedin"
                    value={formData.socials.linkedin}
                    onChange={handleChange}
                  />
                  <input
                    type="url"
                    placeholder="Instagram Profile URL"
                    name="socials.instagram"
                    value={formData.socials.instagram}
                    onChange={handleChange}
                  />
                  <input
                    type="url"
                    placeholder="Twitter Profile URL"
                    name="socials.twitter"
                    value={formData.socials.twitter}
                    onChange={handleChange}
                  />
                </div>

                {formStatus.error && (
                  <div className="form-error">
                    {formStatus.error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="button button--primary"
                  disabled={formStatus.loading}
                >
                  {formStatus.loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Register 